import jexl from 'jexl-extended'
import { Notify } from 'quasar'
import { ref } from 'vue'
import { translate } from 'src/utils/i18n-translate'

/** A JEXL helper implementation, as exported by an extension module. */
export type JexlFunction = (...args: never[]) => unknown

export interface LazyJexlFunctionSpec {
  /** Returned while the extension loads. A function is called with the helper's arguments. */
  pending: unknown
  /** Returned while the last load failed (defaults to `pending`). */
  failed?: unknown
}

export interface LazyJexlExtension {
  /** Only used for diagnostics and tests. */
  id: string
  /** Helper name -> value to return until the real implementation is available. */
  functions: Record<string, LazyJexlFunctionSpec>
  /** Dynamic import of the module that exports `functions` (the real implementations). */
  load: () => Promise<{ functions: Record<string, JexlFunction> }>
}

const resolveValue = (value: unknown, args: unknown[]) =>
  typeof value === 'function' ? (value as (...a: unknown[]) => unknown)(...args) : value

/**
 * Registers JEXL helpers whose implementation lives in a lazily loaded module, so use-case code
 * stays out of the main bundle of environments that never call it.
 *
 * Only the helper names and the `load` reference are registered up front. The first call of any
 * stub starts the dynamic import (once); until it resolves the stub returns the declared pending
 * value. Stubs read a reactive revision, so every computed / watcher that evaluated a JEXL
 * expression through them re-evaluates when the extension has loaded. On load the real
 * implementations replace the stubs. A failed load shows one notification, returns the `failed`
 * values and is retried on the next call (never throws from JEXL, never retries in a loop).
 */
export function registerLazyJexlExtension(extension: LazyJexlExtension) {
  const revision = ref(0)
  const track = () => revision.value
  let loading = false
  let failed = false
  let notified = false
  let loadedFunctions: Record<string, JexlFunction> | undefined

  const onLoaded = (loaded: { functions: Record<string, JexlFunction> }) => {
    loadedFunctions = loaded.functions
    loading = false
    failed = false
    notified = false
    for (const [name, fn] of Object.entries(loaded.functions)) jexl.addFunction(name, fn)
    revision.value++
  }

  const onFailed = (error: unknown) => {
    console.error(`JEXL extension '${extension.id}' failed to load`, error)
    loading = false
    failed = true
    if (notified) return
    notified = true
    Notify.create({ type: 'negative', message: translate('lazyExtension.loadFailed') })
  }

  const start = () => {
    loading = true
    Promise.resolve()
      .then(() => extension.load())
      .then(onLoaded, onFailed)
  }

  const makeStub =
    (name: string, spec: LazyJexlFunctionSpec) =>
    (...args: unknown[]) => {
      // Reading the revision subscribes the surrounding computed / effect to the load.
      track()
      const real = loadedFunctions?.[name] as ((...a: unknown[]) => unknown) | undefined
      if (real) return real(...args)
      if (!loading) start()
      return resolveValue(failed ? (spec.failed ?? spec.pending) : spec.pending, args)
    }

  for (const [name, spec] of Object.entries(extension.functions)) {
    jexl.addFunction(name, makeStub(name, spec))
  }
}
